import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ContentEditor from './ContentEditor';
import MapLocationPicker from './MapLocationPicker';

const RehearsalManager = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [locations, setLocations] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard', 'schedule', 'locations', 'exception'
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [nextRehearsal, setNextRehearsal] = useState(null);
  
  // Form states
  const [editingScheduleDay, setEditingScheduleDay] = useState(null);
  const [newScheduleTime, setNewScheduleTime] = useState('19:00');
  const [newScheduleLocation, setNewScheduleLocation] = useState('');
  
  // Notes editing
  const [showNotesEditor, setShowNotesEditor] = useState(false);
  const [editingScheduleItem, setEditingScheduleItem] = useState(null);
  
  // Schedule editing
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  // Cancellation modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingSchedule, setCancellingSchedule] = useState(null);
  const [cancellationDate, setCancellationDate] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  
  // Location management
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    google_place_id: '',
    latitude: null,
    longitude: null,
    maps_url: '',
    notes: ''
  });
  const [showMapPicker, setShowMapPicker] = useState(false);

  const getDayName = (dayIndex) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return t(`rehearsals.dayNames.${days[dayIndex]}`);
  };

  // Load data when component opens
  useEffect(() => {
    if (isOpen) {
      loadAllData();
    }
  }, [isOpen]);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadLocations(),
        loadSchedule(),
        loadExceptions(),
        calculateNextRehearsal()
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(`${API_BASE}/api/rehearsals?operation=getLocations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        userRole: user.role
      })
    });
    if (response.ok) {
      const data = await response.json();
      setLocations(data);
    }
  };

  const loadSchedule = async () => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(`${API_BASE}/api/rehearsals?operation=getSchedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        userRole: user.role
      })
    });
    if (response.ok) {
      const data = await response.json();
      setSchedule(data);
    }
  };

  const loadExceptions = async () => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(`${API_BASE}/api/rehearsals?operation=getExceptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        userRole: user.role
      })
    });
    if (response.ok) {
      const data = await response.json();
      setExceptions(data);
    }
  };

  const calculateNextRehearsal = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=getNext&timezone=${encodeURIComponent(userTimezone)}`);
      
      if (response.ok) {
        const data = await response.json();
        setNextRehearsal(data);
      } else {
        setNextRehearsal(null);
      }
    } catch (err) {
      console.error('Error calculating next rehearsal:', err);
      setNextRehearsal(null);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Return the time string as-is for display (24-hour format)
    return timeString;
  };

  const handleAddSchedule = async (dayOfWeek) => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=createSchedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          day_of_week: dayOfWeek,
          time: newScheduleTime,
          location_id: newScheduleLocation || locations[0]?.id,
          notes: '',
          notes_html: '',
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await loadSchedule();
        setEditingScheduleDay(null);
        setNewScheduleTime('19:00');
        setNewScheduleLocation('');
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.errors.createSchedule'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateScheduleTime = async (scheduleId, newTime) => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=updateSchedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: scheduleId,
          time: newTime,
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await loadSchedule();
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.errors.updateSchedule'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditScheduleNotes = (scheduleItem) => {
    setEditingScheduleItem(scheduleItem);
    setShowNotesEditor(true);
  };

  const handleEditSchedule = (scheduleItem) => {
    setEditingSchedule({
      ...scheduleItem,
      time: scheduleItem.time || '19:00',
      location_id: scheduleItem.location_id || (locations[0]?.id || ''),
      notes: scheduleItem.notes || '',
      notes_html: scheduleItem.notes_html || ''
    });
    setShowScheduleEditor(true);
  };

  const handleSaveScheduleEdit = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=updateSchedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingSchedule.id,
          time: editingSchedule.time,
          location_id: editingSchedule.location_id,
          notes: editingSchedule.notes,
          notes_html: editingSchedule.notes_html,
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await loadSchedule();
        setShowScheduleEditor(false);
        setEditingSchedule(null);
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.errors.updateSchedule'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScheduleNotes = async (contentData) => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=updateSchedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingScheduleItem.id,
          notes: contentData.contentText,
          notes_html: contentData.contentHtml,
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await loadSchedule();
        
        // Update the editingSchedule state if it's the same item
        if (editingSchedule && editingSchedule.id === editingScheduleItem.id) {
          setEditingSchedule(prev => ({
            ...prev,
            notes: contentData.contentText,
            notes_html: contentData.contentHtml
          }));
        }
        
        setShowNotesEditor(false);
        setEditingScheduleItem(null);
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.errors.updateScheduleNotes'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleItem) => {
    if (!window.confirm(t('rehearsals.confirmations.deleteRehearsal', { day: getDayName(scheduleItem.day_of_week) }))) {
      return;
    }

    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=deleteSchedule&id=${scheduleItem.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await loadSchedule();
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.errors.deleteSchedule'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocation = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=createLocation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newLocation,
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await loadLocations();
        setNewLocation({
          name: '',
          address: '',
          google_place_id: '',
          latitude: null,
          longitude: null,
          maps_url: '',
          notes: ''
        });
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.errors.createLocation'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMapLocationSelected = (locationData) => {
    setNewLocation(prev => ({
      ...prev,
      ...locationData
    }));
    setShowMapPicker(false);
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm(t('rehearsals.confirmations.deleteLocation'))) {
      return;
    }

    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=deleteLocation&id=${locationId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await loadLocations();
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.errors.deleteLocation'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCancelModal = (scheduleItem, date) => {
    setCancellingSchedule(scheduleItem);
    setCancellationDate(date);
    setCancellationReason('');
    setShowCancelModal(true);
  };

  const handleCancelRehearsal = async () => {
    if (!cancellingSchedule || !cancellationDate) return;

    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=createException`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schedule_id: cancellingSchedule.id,
          original_date: cancellationDate,
          exception_type: 'cancelled',
          notes: cancellationReason || 'Rehearsal cancelled',
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await Promise.all([
          loadExceptions(),
          calculateNextRehearsal()
        ]);
        setShowCancelModal(false);
        setCancellingSchedule(null);
        setCancellationDate('');
        setCancellationReason('');
        setError(null);
        // Show success message briefly
        const successDiv = document.createElement('div');
        successDiv.style.cssText = `
          position: fixed; top: 20px; right: 20px; z-index: 9999;
          background: #10b981; color: white; padding: 12px 20px;
          border-radius: 8px; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        `;
        successDiv.textContent = t('rehearsals.cancellationSuccess');
        document.body.appendChild(successDiv);
        setTimeout(() => document.body.removeChild(successDiv), 3000);
      } else {
        const error = await response.json();
        setError(error.error || t('rehearsals.cancellationFailed'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReinstateRehearsal = async (scheduleItem, date) => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      // Find the cancellation exception for this date
      const exception = exceptions.find(e => {
        const exceptionDateStr = new Date(e.original_date).toISOString().split('T')[0];
        return e.schedule_id === scheduleItem.id && 
               exceptionDateStr === date && 
               e.exception_type === 'cancelled';
      });

      if (!exception) {
        setError('Cancellation not found');
        return;
      }

      // Delete the exception to reinstate
      const response = await fetch(`${API_BASE}/api/rehearsals?operation=deleteException&id=${exception.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userRole: user.role
        })
      });

      if (response.ok) {
        await Promise.all([
          loadExceptions(),
          calculateNextRehearsal()
        ]);
        const successDiv = document.createElement('div');
        successDiv.style.cssText = `
          position: fixed; top: 20px; right: 20px; z-index: 9999;
          background: #10b981; color: white; padding: 12px 20px;
          border-radius: 8px; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        `;
        successDiv.textContent = 'Rehearsal reinstated successfully!';
        document.body.appendChild(successDiv);
        setTimeout(() => document.body.removeChild(successDiv), 3000);
      } else {
        const error = await response.json();
        setError(error.error || 'Failed to reinstate rehearsal');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isRehearsalCancelled = (scheduleItem) => {
    const today = new Date();
    const targetDay = scheduleItem.day_of_week;
    const currentDay = today.getDay();
    
    // Check both today (if it's the right day) and next occurrence
    const datesToCheck = [];
    
    // If today is the target day, check today
    if (currentDay === targetDay) {
      const todayStr = today.toISOString().split('T')[0];
      datesToCheck.push(todayStr);
    }
    
    // Always check the next occurrence
    let daysUntil = targetDay - currentDay;
    if (daysUntil <= 0) daysUntil += 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntil);
    const nextDateStr = nextDate.toISOString().split('T')[0];
    datesToCheck.push(nextDateStr);
    
    const cancelled = exceptions.find(e => {
      const exceptionDateStr = new Date(e.original_date).toISOString().split('T')[0];
      return e.schedule_id === scheduleItem.id && 
             datesToCheck.includes(exceptionDateStr) && 
             e.exception_type === 'cancelled';
    });
    
    return cancelled;
  };

  const renderDashboard = () => (
    <div style={{ padding: '24px' }}>
      {/* Next Rehearsal Card */}
      <div style={{
        backgroundColor: '#374151',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid #4b5563'
      }}>
        <h3 style={{ 
          margin: '0 0 12px 0', 
          color: '#f9fafb',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          {t('rehearsals.nextRehearsal')}
        </h3>
        {nextRehearsal ? (
          <div>
            <div style={{ 
              fontSize: '16px', 
              color: '#e5e7eb',
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              📅 {nextRehearsal.date} at {nextRehearsal.time}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#d1d5db',
              marginBottom: '12px'
            }}>
              📍 {nextRehearsal.location?.name || t('rehearsals.locationTBD')}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: nextRehearsal.status === 'confirmed' ? '#10b981' : '#f59e0b',
              fontWeight: '500',
              textTransform: 'uppercase'
            }}>
              ✅ {nextRehearsal.status}
            </div>
          </div>
        ) : (
          <div style={{ color: '#9ca3af', fontSize: '14px' }}>
            {t('rehearsals.noUpcoming')}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <button
          onClick={() => setActiveView('exception')}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
        >
          {t('rehearsals.addException')}
        </button>
        
        <button
          onClick={() => setActiveView('schedule')}
          style={{
            backgroundColor: '#374151',
            color: '#e5e7eb',
            border: '1px solid #4b5563',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#374151'}
        >
          {t('rehearsals.editSchedule')}
        </button>
        
        <button
          onClick={() => setActiveView('locations')}
          style={{
            backgroundColor: '#374151',
            color: '#e5e7eb',
            border: '1px solid #4b5563',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#374151'}
        >
          {t('rehearsals.manageLocations')}
        </button>
      </div>

      {/* Current Schedule Overview */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }}>
        {/* Regular Schedule */}
        <div style={{
          backgroundColor: '#374151',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #4b5563'
        }}>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            color: '#f9fafb',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {t('rehearsals.regularSchedule')}
          </h4>
          {schedule.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {schedule.map(item => (
                <div key={item.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#d1d5db'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <span style={{ minWidth: '60px' }}>
                      📅 {getDayName(item.day_of_week)}
                    </span>
                    <span style={{ minWidth: '60px' }}>
                      {formatTime(item.time)}
                    </span>
                    <span style={{ color: '#9ca3af' }}>
                      📍 {locations.find(l => l.id === item.location_id)?.name || 'Unknown'}
                    </span>
                  </div>
{(() => {
                    const cancelled = isRehearsalCancelled(item);
                    const today = new Date();
                    const targetDay = item.day_of_week;
                    const currentDay = today.getDay();
                    let daysUntil = targetDay - currentDay;
                    if (daysUntil <= 0) daysUntil += 7;
                    const nextDate = new Date(today);
                    nextDate.setDate(today.getDate() + daysUntil);
                    const dateStr = nextDate.toISOString().split('T')[0];
                    
                    return (
                      <button
                        onClick={() => {
                          if (cancelled) {
                            handleReinstateRehearsal(item, dateStr);
                          } else {
                            handleOpenCancelModal(item, dateStr);
                          }
                        }}
                        style={{
                          backgroundColor: cancelled ? '#10b981' : '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '10px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                        title={cancelled ? t('rehearsals.restoreRehearsal') : t('rehearsals.oneTimeCancel')}
                      >
                        {cancelled ? t('rehearsals.undoCancel') : t('rehearsals.oneTimeCancel')}
                      </button>
                    );
                  })()}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>
              No regular schedule set
            </div>
          )}
        </div>

        {/* Upcoming Exceptions */}
        <div style={{
          backgroundColor: '#374151',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #4b5563'
        }}>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            color: '#f9fafb',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {t('rehearsals.upcomingChanges')}
          </h4>
          {exceptions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {exceptions.slice(0, 3).map(exception => (
                <div key={exception.id} style={{
                  fontSize: '12px',
                  color: '#d1d5db'
                }}>
                  <div style={{ fontWeight: '500' }}>
                    {new Date(exception.original_date).toLocaleDateString()}
                  </div>
                  <div style={{ 
                    color: exception.exception_type === 'cancelled' ? '#ef4444' : '#f59e0b',
                    textTransform: 'capitalize'
                  }}>
                    {exception.exception_type.replace('_', ' ')}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>
              {t('rehearsals.noUpcoming')}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderScheduleEditor = () => (
    <div style={{ padding: '24px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <h3 style={{ 
          margin: 0, 
          color: '#f9fafb',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          {t('rehearsals.regularSchedule')}
        </h3>
        <button
          onClick={() => setActiveView('dashboard')}
          style={{
            backgroundColor: '#374151',
            color: '#e5e7eb',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Schedule Grid */}
      <div style={{
        backgroundColor: '#374151',
        borderRadius: '8px',
        border: '1px solid #4b5563',
        overflow: 'hidden'
      }}>
        {Array.from({ length: 7 }, (_, index) => {
          const daySchedule = schedule.find(s => s.day_of_week === index);
          
          return (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr 1fr 100px',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: index < 6 ? '1px solid #4b5563' : 'none',
              gap: '12px'
            }}>
              <div style={{ 
                color: '#f9fafb',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                {getDayName(index)}
              </div>
              
              <div>
                {daySchedule ? (
                  <input
                    type="time"
                    value={daySchedule.time || ''}
                    onChange={(e) => {
                      handleUpdateScheduleTime(daySchedule.id, e.target.value);
                    }}
                    style={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #4b5563',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      color: '#f9fafb',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  />
                ) : editingScheduleDay === index ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <input
                        type="time"
                        value={newScheduleTime}
                        onChange={(e) => setNewScheduleTime(e.target.value)}
                        step="300"
                        style={{
                          backgroundColor: '#1f2937',
                          border: '1px solid #3b82f6',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          color: '#f9fafb',
                          fontSize: '11px',
                          width: '90px'
                        }}
                      />
                      <button
                        onClick={() => handleAddSchedule(index)}
                        style={{
                          backgroundColor: '#10b981',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '4px 6px',
                          color: 'white',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => setEditingScheduleDay(null)}
                        style={{
                          backgroundColor: '#ef4444',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '4px 6px',
                          color: 'white',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <select
                      value={newScheduleLocation}
                      onChange={(e) => setNewScheduleLocation(e.target.value)}
                      style={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #4b5563',
                        borderRadius: '4px',
                        padding: '4px 6px',
                        color: '#f9fafb',
                        fontSize: '10px'
                      }}
                    >
                      <option value="">Select location...</option>
                      {locations.map(location => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingScheduleDay(index);
                      setNewScheduleLocation(locations[0]?.id || '');
                    }}
                    style={{
                      backgroundColor: '#1f2937',
                      border: '1px dashed #4b5563',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      color: '#9ca3af',
                      fontSize: '12px',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    + Add Time
                  </button>
                )}
              </div>
              
              <div style={{ fontSize: '12px', color: '#d1d5db' }}>
                {daySchedule ? (
                  `📍 ${locations.find(l => l.id === daySchedule.location_id)?.name || 'Unknown'}`
                ) : (
                  <span style={{ color: '#9ca3af' }}>No rehearsal</span>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-start', minWidth: '100px' }}>
                {daySchedule && (
                  <>
                    <button
                      onClick={() => handleEditSchedule(daySchedule)}
                      title="Edit rehearsal time, location, and notes"
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(daySchedule)}
                      title="Delete this scheduled rehearsal"
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#ef4444'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                      🗑️
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderLocationManager = () => (
    <div style={{ padding: '24px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <h3 style={{ 
          margin: 0, 
          color: '#f9fafb',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          Manage Locations
        </h3>
        <button
          onClick={() => setActiveView('dashboard')}
          style={{
            backgroundColor: '#374151',
            color: '#e5e7eb',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Add New Location Form */}
      <div style={{
        backgroundColor: '#374151',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid #4b5563'
      }}>
        <h4 style={{ 
          margin: '0 0 16px 0', 
          color: '#f9fafb',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          Add New Location
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <input
            type="text"
            placeholder="Location name"
            value={newLocation.name}
            onChange={(e) => setNewLocation(prev => ({ ...prev, name: e.target.value }))}
            style={{
              backgroundColor: '#1f2937',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              padding: '8px 12px',
              color: '#f9fafb',
              fontSize: '14px'
            }}
          />
          <input
            type="text"
            placeholder="Address"
            value={newLocation.address}
            onChange={(e) => setNewLocation(prev => ({ ...prev, address: e.target.value }))}
            style={{
              backgroundColor: '#1f2937',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              padding: '8px 12px',
              color: '#f9fafb',
              fontSize: '14px'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button
            onClick={() => setShowMapPicker(true)}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🗺️ Select on Map
          </button>
          <input
            type="text"
            placeholder="Or enter Google Maps URL"
            value={newLocation.maps_url}
            onChange={(e) => setNewLocation(prev => ({ ...prev, maps_url: e.target.value }))}
            style={{
              flex: 1,
              backgroundColor: '#1f2937',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              padding: '8px 12px',
              color: '#f9fafb',
              fontSize: '14px'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleCreateLocation}
            disabled={!newLocation.name || !newLocation.address}
            style={{
              backgroundColor: (newLocation.name && newLocation.address) ? '#10b981' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              cursor: (newLocation.name && newLocation.address) ? 'pointer' : 'not-allowed'
            }}
          >
            Add Location
          </button>
          <div style={{ color: '#9ca3af', fontSize: '12px' }}>
            Click "Select on Map" to choose location interactively, or manually enter details
          </div>
        </div>
      </div>

      {/* Existing Locations */}
      <div style={{
        backgroundColor: '#374151',
        borderRadius: '8px',
        border: '1px solid #4b5563'
      }}>
        <div style={{ 
          padding: '16px 20px',
          borderBottom: '1px solid #4b5563',
          color: '#f9fafb',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          Existing Locations
        </div>
        
        {locations.length > 0 ? (
          <div>
            {locations.map(location => (
              <div key={location.id} style={{
                padding: '16px 20px',
                borderBottom: '1px solid #4b5563',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ color: '#f9fafb', fontWeight: '500', marginBottom: '4px' }}>
                    📍 {location.name}
                    {location.is_default && (
                      <span style={{ 
                        color: '#10b981', 
                        fontSize: '12px', 
                        marginLeft: '8px',
                        fontWeight: '400'
                      }}>
                        (Default)
                      </span>
                    )}
                  </div>
                  {location.address && (
                    <div style={{ color: '#9ca3af', fontSize: '13px' }}>
                      {location.address}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {location.maps_url && (
                    <a
                      href={location.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        fontSize: '12px'
                      }}
                    >
                      🗺️ Map
                    </a>
                  )}
                  <button
                    onClick={() => handleDeleteLocation(location.id)}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: '40px 20px',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            No locations added yet
          </div>
        )}
      </div>
    </div>
  );

  const renderExceptionCreator = () => (
    <div style={{ padding: '24px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <h3 style={{ 
          margin: 0, 
          color: '#f9fafb',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          {t('rehearsals.viewExceptions')}
        </h3>
        <button
          onClick={() => setActiveView('dashboard')}
          style={{
            backgroundColor: '#374151',
            color: '#e5e7eb',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Current Exceptions List */}
      <div style={{
        backgroundColor: '#374151',
        borderRadius: '8px',
        border: '1px solid #4b5563',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '16px 20px',
          borderBottom: '1px solid #4b5563',
          color: '#f9fafb',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          Current Exceptions & Changes
        </div>
        
        {exceptions.length > 0 ? (
          <div>
            {exceptions.map(exception => (
              <div key={exception.id} style={{
                padding: '16px 20px',
                borderBottom: '1px solid #4b5563',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#f9fafb'
                    }}>
                      📅 {new Date(exception.original_date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div style={{
                      backgroundColor: exception.exception_type === 'cancelled' ? '#ef4444' : '#f59e0b',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {exception.exception_type.replace('_', ' ')}
                    </div>
                  </div>
                  
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#d1d5db',
                    marginBottom: '4px'
                  }}>
                    Originally: {exception.original_time} at {exception.original_location_name}
                  </div>
                  
                  {exception.exception_type !== 'cancelled' && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#10b981'
                    }}>
                      {exception.exception_type === 'moved' && exception.new_date && (
                        <>📅 Moved to: {new Date(exception.new_date).toLocaleDateString()}</>
                      )}
                      {exception.new_time && (
                        <>🕐 New time: {exception.new_time}</>
                      )}
                      {exception.new_location_name && (
                        <>📍 New location: {exception.new_location_name}</>
                      )}
                    </div>
                  )}
                  
                  {exception.notes && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#9ca3af',
                      marginTop: '8px',
                      fontStyle: 'italic'
                    }}>
                      "{exception.notes}"
                    </div>
                  )}
                </div>
                
                <div style={{ marginLeft: '16px' }}>
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#6b7280',
                    textAlign: 'right'
                  }}>
                    Created: {new Date(exception.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: '40px 20px',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            No exceptions or changes scheduled
            <br />
            <br />
            <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
              Use the "One Time Cancel" buttons in the dashboard to cancel specific rehearsals,<br />
              or create other exceptions like time/location changes.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1f2937',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '1000px',
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151'
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: '#ffffff',
          padding: '16px 24px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {t('rehearsals.title')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              color: 'white',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto',
          backgroundColor: '#1f2937'
        }}>
          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              color: '#9ca3af'
            }}>
              Loading rehearsal data...
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: '#991b1b',
              border: '1px solid #dc2626',
              color: '#fecaca',
              padding: '12px 24px',
              margin: '16px 24px',
              borderRadius: '6px'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {activeView === 'dashboard' && renderDashboard()}
              {activeView === 'schedule' && renderScheduleEditor()}
              {activeView === 'locations' && renderLocationManager()}
              {activeView === 'exception' && renderExceptionCreator()}
            </>
          )}
        </div>
      </div>
      
      {/* Notes Editor Dialog */}
      {showNotesEditor && (
        <div style={{ zIndex: 1200 }}>
          <ContentEditor
            isOpen={showNotesEditor}
            onClose={() => {
              setShowNotesEditor(false);
              setEditingScheduleItem(null);
            }}
            onSave={handleSaveScheduleNotes}
            contentType="rehearsal-notes"
            editingContent={editingScheduleItem ? {
              title: `${getDayName(schedule.find(s => s.id === editingScheduleItem.id)?.day_of_week)} Rehearsal Notes`,
              content_text: editingScheduleItem.notes || '',
              content_html: editingScheduleItem.notes_html || ''
            } : null}
            showScheduling={false}
            showVisibility={false}
            placeholder="Add notes and instructions for this rehearsal..."
          />
        </div>
      )}

      {/* Schedule Editor Dialog */}
      {showScheduleEditor && editingSchedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '1px solid #374151'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              color: '#ffffff',
              padding: '16px 24px',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                ⚙️ Edit {getDayName(editingSchedule.day_of_week)} Rehearsal
              </h3>
              <button onClick={() => setShowScheduleEditor(false)} style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '20px' }}>
              {/* Time Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Rehearsal Time
                </label>
                <input
                  type="time"
                  value={editingSchedule.time || ''}
                  onChange={(e) => setEditingSchedule(prev => ({ ...prev, time: e.target.value }))}
                  step="300"
                  style={{
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    width: '100%'
                  }}
                />
              </div>

              {/* Location Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Location
                </label>
                <select
                  value={editingSchedule.location_id || ''}
                  onChange={(e) => setEditingSchedule(prev => ({ ...prev, location_id: e.target.value }))}
                  style={{
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <option value="">Select a location...</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes Section */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Notes & Instructions
                </label>
                <div style={{
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  padding: '12px',
                  minHeight: '80px',
                  marginBottom: '8px'
                }}>
                  {editingSchedule.notes_html ? (
                    <div style={{ color: '#e5e7eb', fontSize: '14px', lineHeight: '1.5' }} 
                         dangerouslySetInnerHTML={{ __html: editingSchedule.notes_html }} />
                  ) : (
                    <div style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>
                      No notes added yet
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setEditingScheduleItem(editingSchedule);
                    setShowNotesEditor(true);
                  }}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  📝 Edit Notes
                </button>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button onClick={() => setShowScheduleEditor(false)} style={{
                  padding: '10px 20px',
                  backgroundColor: '#374151',
                  color: '#d1d5db',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  Cancel
                </button>
                <button onClick={handleSaveScheduleEdit} style={{
                  padding: '10px 20px',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Map Location Picker */}
      <MapLocationPicker
        isOpen={showMapPicker}
        onClose={() => setShowMapPicker(false)}
        onLocationSelected={handleMapLocationSelected}
        initialLocation={newLocation.latitude ? {
          name: newLocation.name,
          address: newLocation.address,
          lat: newLocation.latitude,
          lng: newLocation.longitude,
          place_id: newLocation.google_place_id
        } : null}
      />

      {/* Cancellation Modal */}
      {showCancelModal && cancellingSchedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '1px solid #374151'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              padding: '16px 24px',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                🚫 {t('rehearsals.cancelRehearsal')}
              </h3>
              <button onClick={() => setShowCancelModal(false)} style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px'
              }}>
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  color: '#f9fafb',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}>
                  {t('rehearsals.confirmCancellation')}
                </div>
                <div style={{
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#e5e7eb',
                  fontSize: '14px'
                }}>
                  📅 {getDayName(cancellingSchedule.day_of_week)} rehearsal on {new Date(cancellationDate).toLocaleDateString()}
                  <br />
                  🕐 {formatTime(cancellingSchedule.time)}
                  <br />
                  📍 {locations.find(l => l.id === cancellingSchedule.location_id)?.name || 'Unknown location'}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {t('rehearsals.cancellationReason')}
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder={t('rehearsals.reasonPlaceholder')}
                  style={{
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    width: '100%',
                    minHeight: '80px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button 
                  onClick={() => setShowCancelModal(false)} 
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#374151',
                    color: '#d1d5db',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {t('rehearsals.cancel')}
                </button>
                <button 
                  onClick={handleCancelRehearsal}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#6b7280' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Cancelling...' : t('rehearsals.cancelRehearsal')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RehearsalManager;